import React from 'react';
import {Form, Input, Select, Radio, DatePicker, Row, Col} from 'antd';
import ConditionalFormItem from '../../ConditionalFormItem';
import MediaSelector from '../../MediaLibrary/MediaSelector';
import { CLASS_OPTIONS } from '../../../config/classes';
import { SERVICE_TYPE_MAP } from '../../../config/service-type-map';
import NATION_OPTIONS from "../../../config/nations";

const { Option } = Select;
const { TextArea } = Input;

interface TrademarkBusinessTabProps {
  form: any;
  serviceType: string;
  getReadOnlyProps: (fieldType?: 'input' | 'select') => any;
  baseCountryMode?: boolean;
}

const TrademarkSection: React.FC<TrademarkBusinessTabProps> = ({ form, serviceType, getReadOnlyProps, baseCountryMode = false }) => {
  const serviceTypeOptions = SERVICE_TYPE_MAP[serviceType] || {};

  return (
    <>
      {/* 基本信息（复制自 BasicInfoTab，去除申请人相关） */}

      <Form.Item label="子业务名称" name="sub_service" rules={[{ required: true, message: '请选择子业务名称' }]}>
        <Select placeholder="请选择具体的子业务名称" data-testid="order-sub-service">
          {(Object.entries(serviceTypeOptions) as [string, string][]).map(([key, value]) => (
            <Option key={key} value={key}>
              {value}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label={baseCountryMode ? '基础国家/地区' : '国家/地区'}
        name="nation"
        rules={[{ required: true, message: baseCountryMode ? '请选择基础国家/地区' : '请选择国家/地区' }]}
      >
        <Select
          placeholder={baseCountryMode ? '请选择基础国家/地区' : '请选择国家/地区'}
          showSearch
          filterOption={(input, option) =>
            String(option?.children || '').toLowerCase().includes(input.toLowerCase())
          }
          data-testid="order-nation"
        >
          {NATION_OPTIONS.map(option => (
            <Option key={option.value} value={option.value}>
              {option.label}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label="订单编号" name="order_no" rules={[{ required: true, message: '订单编号自动生成' }]}>
            <Input placeholder="自动生成" readOnly style={{ backgroundColor: '#f5f5f5', color: '#999' }} data-testid="order-no" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="客户案件编号" name="case_no">
            <Input placeholder="客户内部/对外案件编号（可选）" data-testid="order-case-no" />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item label={baseCountryMode ? '基础国商标名' : '商标名'} name="trademark_name" rules={[{ required: true, message: '请输入商标名' }]}>
        <Input placeholder="请输入商标名" data-testid="order-trademark-name" />
      </Form.Item>

      {/* 所有商标子服务都需要维护商标图样，添加和编辑共用此字段。 */}
      <Form.Item name="trademark_image" hidden />
      <Form.Item noStyle shouldUpdate>
        {() => {
          const value = form.getFieldValue('trademark_image');
          const selectedIds = Array.isArray(value) ? value : value ? [value] : [];
          return (
            <Form.Item label="商标图样">
              <MediaSelector
                multiple={false}
                placeholder="上传或选择商标图样"
                fileTypes={['image']}
                showPreview
                value={selectedIds}
                onChange={(v?: number | number[]) => {
                  // 使用 null 而非 undefined，确保 Antd setFieldsValue 能正确清空字段
                  // （Antd 的 setFieldsValue 会忽略 undefined 值，导致图片删除后字段无法被清除）
                  const id = Array.isArray(v) ? (v[0] ?? null) : (v ?? null);
                  form.setFieldsValue({ trademark_image: id });
                }}
              />
            </Form.Item>
          );
        }}
      </Form.Item>



      <ConditionalFormItem condition="sub_service == registration">
        <Form.Item label="优先权">
          <Form.Item name="priority" noStyle initialValue={'无优先权'}>
            <Radio.Group>
              <Radio value="有优先权">有优先权</Radio>
              <Radio value="无优先权">无优先权</Radio>
            </Radio.Group>
          </Form.Item>
        </Form.Item>

        <ConditionalFormItem condition="priority == 有优先权">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="申请号" name="priority_no">
                <Input placeholder="请输入优先权申请号" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="优先权申请日期" name="priority_date">
                <DatePicker style={{ width: '100%' }} placeholder="请输入优先权申请日期" />
              </Form.Item>
            </Col>
          </Row>
        </ConditionalFormItem>
      </ConditionalFormItem>


      {/* 美国申请基础（仅美国+注册显示） */}
      <ConditionalFormItem condition="orders.nation == 美国 && sub_service == registration">
        <Form.Item label="申请基础" name="application_basics" rules={[{ required: true, message: '请选择申请基础' }]}>
          <Radio.Group>
            <Radio value="已使用1(a)">已使用1(a)</Radio>
            <Radio value="意向使用1(b)">意向使用1(b)</Radio>
            <Radio value="本国注册44(e)">本国注册44(e)</Radio>
            <Radio value="本国申请44(d)">本国申请44(d)</Radio>
          </Radio.Group>
        </Form.Item>

        <ConditionalFormItem condition="application_basics == 已使用1(a)">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="首次使用日期" name="a1_first_use_at">
                <DatePicker style={{ width: '100%' }} placeholder="请选择日期" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="首次商用日期" name="a1_first_commercial_use_at">
                <DatePicker style={{ width: '100%' }} placeholder="请选择日期" />
              </Form.Item>
            </Col>
          </Row>
        </ConditionalFormItem>

        <ConditionalFormItem condition="application_basics == 本国注册44(e)">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="基础商标申请号" name="d44_application_no">
                <Input placeholder="请输入基础商标申请号" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="基础商标申请日期" name="d44_application_at">
                <DatePicker style={{ width: '100%' }} placeholder="请申请日期" />
              </Form.Item>
            </Col>
          </Row>
        </ConditionalFormItem>

        <ConditionalFormItem condition="application_basics == 本国申请44(d)">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="基础商标注册号" name="b44_registration_no">
                <Input placeholder="请输入基础商标注册号" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="基础商标注册日期" name="b44_registration_at">
                <DatePicker style={{ width: '100%' }} placeholder="请选择日期" />
              </Form.Item>
            </Col>
          </Row>

        </ConditionalFormItem>
      </ConditionalFormItem>


      {/* 商标含义/颜色声明（仅注册）*/}
      <ConditionalFormItem condition="sub_service == registration">
        <Form.Item label="商标含义" name="trademark_meaning">
          <TextArea placeholder="商标如有含义请注明" rows={3} data-testid="order-trademark-meaning" />
        </Form.Item>
        <Form.Item label="颜色声明" name="color_statement">
          <TextArea placeholder="如有颜色声明请注明" rows={3} data-testid="order-trademark-statement" />
        </Form.Item>
      </ConditionalFormItem>

      <ConditionalFormItem condition="sub_service == registration || sub_service == transactions || sub_service == investment">
        <Form.Item label="商标类型" name="trademark_type" initialValue={"标准文字"} rules={[{ required: true, message: '请选择商标类型' }]}>
          <Radio.Group data-testid="order-trademark-type">
            {['标准文字', '设计文字', '纯图商标', '图文组合'].map((label) => (
              <Radio key={label} value={label}>{label}</Radio>
            ))}
          </Radio.Group>
        </Form.Item>
      </ConditionalFormItem>

      {/* 类别支持多选：将选中的多个类别在保存时转换为字符串保存 */}
      <ConditionalFormItem condition="sub_service != extension">
        <Form.Item label="大类" name="primary_class">
          <Select
            mode="multiple"
            placeholder="请选择商标大类（可多选）"
            data-testid="order-primary-class"
            showSearch
            filterOption={(input, option) => String(option?.children || '').toLowerCase().includes(input.toLowerCase())}
            maxTagCount="responsive"
          >
            {CLASS_OPTIONS.map((opt: any) => (
              <Option key={opt.value} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </ConditionalFormItem>

      {/* 所有商标子服务都需要维护小类，避免切换子服务后字段无法填写或编辑。 */}
      <Form.Item label="小类" name="sub_class" >
        <TextArea placeholder="请输入确认后的英文小类，以；分隔" data-testid="order-sub-class" rows={3} />
      </Form.Item>


      {/* 商标状态（通用） */}
      <ConditionalFormItem condition="sub_service == transfer">
        <Form.Item label="商标状态" name="trademark_status"  data-testid="order-trademark-status">
          <Radio.Group>
            <Radio value="申请中">申请中</Radio>
            <Radio value="已下证">已下证</Radio>
          </Radio.Group>
        </Form.Item>
      </ConditionalFormItem>

      {/* 商标仅转让（transfer） */}
      <ConditionalFormItem condition="sub_service == transfer">
        {/* 转让类型字段：应绑定到 assignment_type，而不是 change_items */}
        <Form.Item label="转让类型" name="assignment_type" rules={[{ required: true, message: '转让类型' }]}>
          <Radio.Group>
            <Radio value="整体转让">整体转让</Radio>
            <Radio value="部分转让">部分转让</Radio>
          </Radio.Group>
        </Form.Item>
      </ConditionalFormItem>


      <Row gutter={16}>
        <Col span={12}>
            <Form.Item label={baseCountryMode ? '基础国申请号' : '申请号'} name="application_no" data-testid="order-application-no">
              <Input placeholder={baseCountryMode ? '请输入基础国申请号' : '请输入申请号'} />
            </Form.Item>
        </Col>

        <Col span={12}>
            <Form.Item label="注册号" name="registration_no">
              <Input placeholder="请输入注册号" data-testid="order-registration-no" />
            </Form.Item>
        </Col>
      </Row>

      <ConditionalFormItem condition="sub_service == investment">
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item label="出售状态" name="sale_status">
              <Radio.Group>
                {/* 投资订单既可能在售/已售，也可能暂时不可售，表单需要完整暴露这三种业务状态。 */}
                <Radio value="在售">在售</Radio>
                <Radio value="已出售">已出售</Radio>
                <Radio value="不可售">不可售</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="出售时间" name="sold_at">
              <DatePicker style={{ width: '100%' }} placeholder="请选择出售时间" />
            </Form.Item>
          </Col>
        </Row>
      </ConditionalFormItem>

      {/* 撤销无效/提异议（revocation） */}
      <ConditionalFormItem condition="sub_service == revocation">
        <Form.Item label="撤销类型" name="revocation_type" rules={[{ required: true, message: '请选择撤销类型' }]}>
          <Radio.Group>
            <Radio value="提异议">提异议</Radio>
            <Radio value="提撤销">提撤销</Radio>
            <Radio value="提无效">提无效</Radio>
          </Radio.Group>
        </Form.Item>
      </ConditionalFormItem>

      <ConditionalFormItem condition="sub_service == transactions || sub_service == transfer">
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="受让人名字" name="assignee_name" data-testid="order-assignee-name">
              <Input placeholder="请输入受让人名字" />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label="受让人英文名字" name="assignee_name_en">
              <Input placeholder="请输入受让人英文名字" data-testid="order-assignee-name-en" />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item label="受让人地址" name="assignee_address">
              <Input placeholder={baseCountryMode ? '请输入受让人地址' : '请输入受让人地址'} data-testid="order-assignee-address" />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item label="受让人英文地址" name="assignee_address_en">
              <Input placeholder="请输入受让人英文地址" data-testid="order-assignee-address-en" />
            </Form.Item>
          </Col>

          <ConditionalFormItem condition="sub_service == transactions">
            <Col span={24}>
              <Form.Item label="受让主体类型" name="assignee_entity_type" initialValue={"公司申请"} rules={[{ required: true, message: '请选择受让主体类型' }]}>
                <Radio.Group data-testid="order-assignee-entity-type">
                  {['公司申请', '个人申请'].map((label) => (
                    <Radio key={label} value={label}>{label}</Radio>
                  ))}
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="是否含转让" name="include_transfer_service" initialValue={"不含转让"} rules={[{ required: true, message: '请选择是否含转让' }]}>
                <Radio.Group data-testid="order-include-transfer-service">
                  {['含转让', '不含转让'].map((label) => (
                    <Radio key={label} value={label}>{label}</Radio>
                  ))}
                </Radio.Group>
              </Form.Item>
            </Col>
          </ConditionalFormItem>
        </Row>
      </ConditionalFormItem>

      {/* 商标延期申请（extension） */}
      <ConditionalFormItem condition="sub_service == extension">
        <Form.Item label="延期类型" name="postpone_type" rules={[{ required: true, message: '请选择延期类型' }]}>
          <Radio.Group>
            <Radio value="商标复审延期">商标复审延期</Radio>
            <Radio value="商标使用声明提交延期">商标使用声明提交延期</Radio>
            <Radio value="提出公告延期">提出公告延期</Radio>
            <Radio value="其他延期">其他延期</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item label="延期次数" name="postpone_times" rules={[{ required: true, message: '请选择延期次数' }]}>
          <Radio.Group>
            <Radio value="第1次">第1次</Radio>
            <Radio value="第2次">第2次</Radio>
            <Radio value="第3次">第3次</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item label="延期原因" name="postpone_reason" rules={[{ required: true, message: '请输入延期原因' }]}>
          <TextArea rows={3} placeholder="请填写延期申请的原因" />
        </Form.Item>
      </ConditionalFormItem>

      {/* 商标复审（review） */}
      <ConditionalFormItem condition="sub_service == review">
        <Form.Item label="复审类型" name="review_type" rules={[{ required: true, message: '请选择复审类型' }]}>
          <Radio.Group>
            <Radio value="被驳回">被驳回</Radio>
            <Radio value="被异议">被异议</Radio>
            <Radio value="被无效">被无效</Radio>
            <Radio value="被撤销">被撤销</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item label="复审次数" name="review_times" rules={[{ required: true, message: '请选择复审次数' }]}>
          <Radio.Group>
            <Radio value="第1次">第1次</Radio>
            <Radio value="第2次">第2次</Radio>
            <Radio value="第3次">第3次</Radio>
          </Radio.Group>
        </Form.Item>
      </ConditionalFormItem>


      {/* 理由基础（复审/撤销无效 共用） */}
      <ConditionalFormItem condition="sub_service == review || sub_service == revocation">
        <Form.Item label="理由基础" name="rationale_basis" rules={[{ required: true, message: '请选择理由基础' }]}>
          <Select placeholder="请选择理由基础">
            {['本国在先使用', '本国在先权利', '国外在先使用', '国外在先权利', '其他', '恶意注册', '权利基础不符合规定'].map((v) => (
              <Option key={v} value={v}>
                {v}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </ConditionalFormItem>

      {/* 商标检索（search） */}
      <ConditionalFormItem condition="sub_service == search">
        <Form.Item label="检索方式" name="search_method" rules={[{ required: true, message: '请选择检索方式' }]}>
          <Radio.Group data-testid="order-search-method">
            <Radio value="官方检索">官方检索</Radio>
            <Radio value="律师检索">律师检索</Radio>
            <Radio value="代理检索">代理检索</Radio>
          </Radio.Group>
        </Form.Item>
      </ConditionalFormItem>

      {/* 商标宣誓（oath） */}
      <ConditionalFormItem condition="sub_service == oath">
        <Form.Item label="宣誓类型" name="oath_type" rules={[{ required: true, message: '请选择宣誓类型' }]}>
          <Select placeholder="请选择宣誓类型">
            {['第3年', '第5年', '第10年', '第15年', '第20年'].map((v) => (
              <Option key={v} value={v}>
                {v}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </ConditionalFormItem>

      {/* 商标续展（renewal） */}
      <ConditionalFormItem condition="sub_service == renewal">
        <Form.Item label="续展期次" name="renewal_period" rules={[{ required: true, message: '请选择续展期次' }]}>
          <Radio.Group>
            <Radio value="第1期">第1期</Radio>
            <Radio value="第2期">第2期</Radio>
            <Radio value="第3期">第3期</Radio>
            <Radio value="第4期">第4期</Radio>
          </Radio.Group>
        </Form.Item>
      </ConditionalFormItem>

      {/* 商标转代理（agency_transfer） */}
      <ConditionalFormItem condition="sub_service == agency_transfer">
        <Form.Item label="原代理人" name="original_agent" rules={[{ required: true, message: '请输入原代理人' }]}>
          <Input placeholder="请输入原代理人" />
        </Form.Item>
      </ConditionalFormItem>

      {/* 商标注销（cancellation） */}
      <ConditionalFormItem condition="sub_service == cancellation">
        <Form.Item label="注销原因" name="cancellation_reason" rules={[{ required: true, message: '请输入注销原因' }]}>
          <Input placeholder="请输入注销原因" />
        </Form.Item>
      </ConditionalFormItem>

      {/* 商标补证书（certificate_reissue） */}
      <ConditionalFormItem condition="sub_service == certificate_reissue">
        <Form.Item label="证书类型" name="certificate_type" rules={[{ required: true, message: '请选择证书类型' }]}>
          <Radio.Group>
            <Radio value="电子证书">电子证书</Radio>
            <Radio value="纸质证书">纸质证书</Radio>
          </Radio.Group>
        </Form.Item>
      </ConditionalFormItem>

      {/* 信息变更（information_changes） */}
      <ConditionalFormItem condition="sub_service == information_changes">
        <Form.Item label="变更范围" name="change_items" rules={[{ required: true, message: '请选择变更范围' }]}>
          <Radio.Group>
            <Radio value="名字">名字</Radio>
            <Radio value="地址">地址</Radio>
            <Radio value="名字 + 地址">名字 + 地址</Radio>
          </Radio.Group>
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="变更后名字" name="new_applicant_name">
              <Input placeholder="请输入变更后的名字" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="变更后英文名字" name="new_applicant_name_en">
              <Input placeholder="请输入变更后的英文名字" />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item label="变更后地址" name="new_applicant_address">
              <Input placeholder="请输入变更后的地址" />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="变更后英文地址" name="new_applicant_address_en">
              <Input placeholder="请输入变更后的英文地址" />
            </Form.Item>
          </Col>
        </Row>
      </ConditionalFormItem>

      <Form.Item label="备注" name="remark">
        <TextArea rows={3} placeholder="请输入备注信息" data-testid="order-remark" />
      </Form.Item>
    </>
  );
};

export default TrademarkSection;
